import { useState } from 'react';
import { signedIn } from '../../data/session';
import { api } from '../../lib/api';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import s from './Auth.module.css';
import { AuthLayout } from './AuthLayout';

/**
 * Where to go after signing in, when a page outside the app sent us here (connecting Claude
 * goes through /authorize). Only same-origin paths on that page are followed.
 */
function continueTo(): string | null {
  const next = new URLSearchParams(location.search).get('next');
  return next && /^\/(authorize(\?|$)|android$)/.test(next) ? next : null;
}

async function finish() {
  const next = continueTo();
  if (next) {
    location.assign(next);
    return;
  }
  await signedIn();
}

export function SignInScreen() {
  const [mode, setMode] = useState<'passkey' | 'password'>('passkey');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const passkey = async () => {
    setBusy(true);
    setError(null);
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const { challengeId, options } = await api<{
        challengeId: string;
        options: Parameters<typeof startAuthentication>[0]['optionsJSON'];
      }>('/api/auth/passkey/options', { body: {} });
      const response = await startAuthentication({ optionsJSON: options });
      await api('/api/auth/passkey/verify', { body: { challengeId, response } });
      await finish();
    } catch (e) {
      const msg = (e as Error).message;
      setError(
        /NotAllowed|cancel|abort/i.test(msg)
          ? 'Sign-in was cancelled. Try again, or use your password.'
          : msg,
      );
    } finally {
      setBusy(false);
    }
  };

  const withPassword = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password', { body: { password, code } });
      await finish();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome back">
      {mode === 'passkey' ? (
        <div className={s.steps}>
          <p className="muted">Sign in with the passkey on this computer.</p>
          <Button
            variant="primary"
            icon="fingerprint"
            wide
            onClick={() => void passkey()}
            disabled={busy}
          >
            Sign in with passkey
          </Button>
          {error && <p className={`${s.error} t-footnote`}>{error}</p>}
          <button type="button" className={s.link} onClick={() => setMode('password')}>
            Use password and code
          </button>
        </div>
      ) : (
        <form
          className={s.steps}
          onSubmit={(e) => {
            e.preventDefault();
            void withPassword();
          }}
        >
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
          <TextField
            label="Code from your authenticator"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
            className="num"
          />
          {error && <p className={`${s.error} t-footnote`}>{error}</p>}
          <Button
            variant="primary"
            type="submit"
            wide
            disabled={busy || !password || code.length !== 6}
          >
            Sign in
          </Button>
          <button type="button" className={s.link} onClick={() => setMode('passkey')}>
            Use a passkey instead
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
